import React, { useState, Fragment } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch, FieldErrors } from 'react-hook-form';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { banks, Bank } from '@/data/banks';
import { FormInput } from '../FormInput';
import { RequiredIndicator } from '../RequiredIndicator';
import { getErrorMessage as getErr } from '@/utils/form-validation/schemaHelper';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

export interface BankAccountFieldSetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<any>;
    setValue: UseFormSetValue<any>;
    watch: UseFormWatch<any>;
    errors?: FieldErrors<any>;
    isSubmitting?: boolean;
    layout?: 'compact' | 'full';
    /**
     * Quando true, esconde totalmente a seção de Conta Salário.
     * Útil em contextos onde o usuário não possui conta BMG.
     */
    hideSalaryAccount?: boolean;
}

const BankAccountFieldSet: React.FC<BankAccountFieldSetProps> = ({
    context,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full',
    hideSalaryAccount = false
}) => {
    const [salaryBankQuery, setSalaryBankQuery] = useState('');
    const [personalBankQuery, setPersonalBankQuery] = useState('');

    // Verificar se os campos devem ser mostrados no contexto atual
    const shouldShowSalaryAccount = !hideSalaryAccount && collaboratorSchemaHelpers.shouldShowField('salaryAccount', context);
    const shouldShowPersonalAccount = collaboratorSchemaHelpers.shouldShowField('personalAccount', context);

    // Verificar se os campos são obrigatórios no contexto atual
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    // Reusable helper to extract error message using global utility
    const getErrorMessage = (fieldPath: string): string | undefined => getErr(errors, fieldPath);

    // Helper para renderizar um combobox de banco
    const renderBankCombobox = (
        accountType: 'salaryAccount' | 'personalAccount',
        title: string,
        query: string,
        setQuery: (value: string) => void
    ) => {
        const fieldName = `${accountType}.bankCode`;
        const currentBankCode = watch(fieldName);
        const selectedBank = banks.find(b => b.code === currentBankCode) || null;

        const filteredBanks = query === ''
            ? banks
            : banks.filter((bank) =>
                bank.name.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, '')) ||
                bank.code.includes(query)
            );

        return (
            <Controller
                name={fieldName}
                control={control}
                render={({ field }) => (
                    <Combobox
                        value={selectedBank}
                        onChange={(bank: Bank | null) => {
                            setValue(fieldName, bank ? bank.code : '');
                        }}
                    >
                        <div className="relative">
                            <Combobox.Label className="block text-sm font-medium text-gray-700">
                                Banco {isRequired(fieldName) && <RequiredIndicator />}
                            </Combobox.Label>
                            <div className="relative mt-1">
                                <Combobox.Input
                                    className={`w-full rounded-md border bg-white py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm ${getErrorMessage(fieldName) ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    displayValue={(bank: Bank) => bank ? `${bank.code} - ${bank.name}` : ''}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                                    placeholder="Digite para pesquisar o banco"
                                    disabled={isSubmitting}
                                    data-testid={`bank-combobox-${accountType}`}
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                            </div>
                            <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => setQuery('')}
                            >
                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                    {filteredBanks.length === 0 && query !== '' ? (
                                        <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                            Nenhum banco encontrado.
                                        </div>
                                    ) : (
                                        filteredBanks.map((bank) => (
                                            <Combobox.Option
                                                key={bank.code}
                                                className={({ active }) =>
                                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                                    }`
                                                }
                                                value={bank}
                                            >
                                                {({ selected, active }) => (
                                                    <>
                                                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                                            {bank.code} - {bank.name}
                                                        </span>
                                                        {selected ? (
                                                            <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'
                                                                }`}>
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                            </span>
                                                        ) : null}
                                                    </>
                                                )}
                                            </Combobox.Option>
                                        ))
                                    )}
                                </Combobox.Options>
                            </Transition>
                            <ErrorMessage errors={errors} fieldPath={fieldName} />
                        </div>
                    </Combobox>
                )}
            />
        );
    };

    // Helper para renderizar os campos de uma conta
    const renderAccountFields = (accountType: 'salaryAccount' | 'personalAccount', title: string) => {
        return (
            <fieldset className="border p-4 rounded-md" data-testid={`fieldset-${accountType}`}>
                <legend className="text-sm font-medium px-1 text-gray-400">{title}</legend>
                <div className={`pt-2 ${layout === 'compact' ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}`}>
                    {/* Banco */}
                    <div className="md:col-span-2">
                        {renderBankCombobox(
                            accountType,
                            title,
                            accountType === 'salaryAccount' ? salaryBankQuery : personalBankQuery,
                            accountType === 'salaryAccount' ? setSalaryBankQuery : setPersonalBankQuery
                        )}
                    </div>

                    {/* Agência */}
                    <Controller
                        name={`${accountType}.agency`}
                        control={control}
                        render={({ field }) => (
                            <FormInput
                                label="Agência"
                                name={`${accountType}.agency`}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value)}
                                placeholder="Ex: 0123"
                                showRequiredIndicators={isRequired(`${accountType}.agency`)}
                                error={getErrorMessage(`${accountType}.agency`)}
                                dataTestId={`agency-input-${accountType}`}
                                disabled={isSubmitting}
                            />
                        )}
                    />

                    {/* Conta */}
                    <Controller
                        name={`${accountType}.account`}
                        control={control}
                        render={({ field }) => (
                            <FormInput
                                label="Conta"
                                name={`${accountType}.account`}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value)}
                                placeholder="Ex: 12345"
                                showRequiredIndicators={isRequired(`${accountType}.account`)}
                                error={getErrorMessage(`${accountType}.account`)}
                                dataTestId={`account-input-${accountType}`}
                                disabled={isSubmitting}
                            />
                        )}
                    />

                    {/* Dígito */}
                    <Controller
                        name={`${accountType}.digit`}
                        control={control}
                        render={({ field }) => (
                            <FormInput
                                label="Dígito"
                                name={`${accountType}.digit`}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value)}
                                placeholder="Ex: 6"
                                showRequiredIndicators={isRequired(`${accountType}.digit`)}
                                error={getErrorMessage(`${accountType}.digit`)}
                                dataTestId={`digit-input-${accountType}`}
                                disabled={isSubmitting}
                            />
                        )}
                    />

                    {/* Chave Pix */}
                    <Controller
                        name={`${accountType}.pixKey`}
                        control={control}
                        render={({ field }) => (
                            <FormInput
                                label="Chave Pix"
                                name={`${accountType}.pixKey`}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value)}
                                placeholder="CPF, e-mail, telefone, etc."
                                dataTestId={`pixKey-input-${accountType}`}
                                disabled={isSubmitting}
                            />
                        )}
                    />
                </div>
            </fieldset>
        );
    };

    return (
        <div className="space-y-6" data-testid="bank-account-fieldset">
            {shouldShowSalaryAccount && renderAccountFields('salaryAccount', 'Conta Salário')}
            {shouldShowPersonalAccount && renderAccountFields('personalAccount', 'Conta Pessoal')}
        </div>
    );
};

export default BankAccountFieldSet; 